import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { UPDATE_VOTING_ARTICLE, MARK_ARTICLE_REPORTS_AS_FALSE_ALARM } from '../graphql/voting';

const VotingEditModal = ({ isOpen, onClose, article, onSuccess }) => {
  const { user } = useAuth();
  const isSemiAdmin = user?.isSemiAdmin || false;
  const [editForm, setEditForm] = useState({
    status: 'PUBLISHED',
    allowComments: true,
    showVoteResults: true
  });
  const [isMarkingFalseAlarm, setIsMarkingFalseAlarm] = useState(false);

  // 當modal打開或article改變時，更新表單數據
  useEffect(() => {
    if (isOpen && article) {
      setEditForm({
        status: article.status || 'PUBLISHED',
        allowComments: article.allowComments ?? true,
        showVoteResults: article.showVoteResults ?? true
      });
    }
  }, [isOpen, article]);

  const [updateVotingArticle] = useMutation(UPDATE_VOTING_ARTICLE, {
    onCompleted: (data) => {
      if (data.updateVotingArticle.success) {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        alert('更新失敗：' + data.updateVotingArticle.message);
      }
    },
    onError: (error) => {
      console.error('更新失敗:', error);
      alert('更新失敗：' + error.message);
    }
  });

  const [markAsFalseAlarm] = useMutation(MARK_ARTICLE_REPORTS_AS_FALSE_ALARM, {
    onCompleted: (data) => {
      if (data.markArticleReportsAsFalseAlarm.success) {
        alert(data.markArticleReportsAsFalseAlarm.message);
        setIsMarkingFalseAlarm(false);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        alert('標記誤報失敗：' + data.markArticleReportsAsFalseAlarm.message);
        setIsMarkingFalseAlarm(false);
      }
    },
    onError: (error) => {
      console.error('標記誤報失敗:', error);
      alert('標記誤報失敗：' + error.message);
      setIsMarkingFalseAlarm(false);
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // 將字符串 "true"/"false" 轉換為布爾值
    let processedValue = value;
    if (value === 'true') processedValue = true;
    if (value === 'false') processedValue = false;
    
    setEditForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSave = async () => {
    try {
      // 一般使用者不能將狀態設為 REPORTED，也不能修改已經是 REPORTED 的狀態
      let finalStatus = editForm.status;
      if (!isSemiAdmin) {
        if (finalStatus === 'REPORTED' || article?.status === 'REPORTED') {
          // 一般使用者不能設置或修改 REPORTED 狀態
          finalStatus = article?.status || 'PUBLISHED';
        }
      }
      
      await updateVotingArticle({
        variables: {
          input: {
            id: article.id,
            status: finalStatus,
            allowComments: editForm.allowComments,
            showVoteResults: editForm.showVoteResults
          }
        }
      });
    } catch (error) {
      console.error('保存失敗:', error);
    }
  };

  const handleMarkAsFalseAlarm = async () => {
    if (!window.confirm('確定要將此文章的所有檢舉標記為誤報嗎？')) {
      return;
    }
    
    setIsMarkingFalseAlarm(true);
    try {
      await markAsFalseAlarm({
        variables: {
          articleId: article.id
        }
      });
    } catch (error) {
      console.error('標記誤報失敗:', error);
    }
  };

  // 檢查文章是否有檢舉（可以通過 reportCount 或狀態判斷）
  // 如果文章狀態是 REPORTED，或者有 reportCount，則認為有檢舉
  const hasReports = (article?.reportCount && article.reportCount > 0) || article?.status === 'REPORTED';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">編輯投票文</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              狀態
            </label>
            {article?.status === 'REPORTED' && !isSemiAdmin ? (
              // 一般使用者看到被檢舉狀態時，顯示為鎖定（不可編輯）
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600">
                被檢舉（已鎖定）
              </div>
            ) : (
              <select
                name="status"
                value={editForm.status}
                onChange={handleInputChange}
                disabled={article?.status === 'REPORTED' && !isSemiAdmin}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
              >
                <option value="PUBLISHED">進行中</option>
                <option value="CLOSED">已結束</option>
                {isSemiAdmin && <option value="REPORTED">被檢舉</option>}
              </select>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              允許評論
            </label>
            <select
              name="allowComments"
              value={editForm.allowComments.toString()}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="true">是</option>
              <option value="false">否</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              顯示投票結果
            </label>
            <select
              name="showVoteResults"
              value={editForm.showVoteResults.toString()}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="true">是</option>
              <option value="false">否</option>
            </select>
          </div>
        </div>

        {/* 標記誤報按鈕（僅半管理員，且文章有檢舉時顯示） */}
        {isSemiAdmin && hasReports && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleMarkAsFalseAlarm}
              disabled={isMarkingFalseAlarm}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isMarkingFalseAlarm ? '處理中...' : '將所有檢舉標記為誤報'}
            </button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              此操作會將此文章的所有檢舉記錄標記為誤報
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default VotingEditModal;

